import React, { useState } from 'react'
import { ChevronDown, Plus, Check, Pencil, Trash2 } from 'lucide-react'
import { OrgChartVersion } from '../../services/orgChartService'

interface OrgChartSwitcherProps {
  charts: OrgChartVersion[]
  activeChartId: string | null
  isAdmin: boolean
  busy?: boolean
  onSelect: (chartId: string) => void
  onCreateDraft: () => void
  onPromoteToLive: () => void
  onRename: (chartId: string, name: string) => void
  onDeleteDraft: (chartId: string) => void
}

export const OrgChartSwitcher: React.FC<OrgChartSwitcherProps> = ({
  charts,
  activeChartId,
  isAdmin,
  busy = false,
  onSelect,
  onCreateDraft,
  onPromoteToLive,
  onRename,
  onDeleteDraft
}) => {
  const [open, setOpen] = useState(false)
  const active = charts.find(c => c.id === activeChartId) || null
  const isViewingDraft = active?.status === 'draft'

  const handleRename = (chart: OrgChartVersion) => {
    const next = window.prompt('Rename org chart', chart.name)
    if (next == null) return
    const trimmed = next.trim()
    if (!trimmed || trimmed === chart.name) return
    onRename(chart.id, trimmed)
  }

  const handleDelete = (chart: OrgChartVersion) => {
    if (chart.status === 'live') return
    if (!confirm(`Delete draft “${chart.name}”? This cannot be undone.`)) return
    onDeleteDraft(chart.id)
  }

  return (
    <div className="relative flex items-center gap-2 min-w-0">
      <div className="relative">
        <button
          type="button"
          disabled={busy || charts.length === 0}
          onClick={() => setOpen(v => !v)}
          className="inline-flex items-center gap-2 max-w-[18rem] px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {active?.status === 'live' ? (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-green-100 text-green-800">
              Live
            </span>
          ) : (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-amber-100 text-amber-800">
              Draft
            </span>
          )}
          <span className="truncate font-medium">
            {active?.name || 'Select chart'}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        {open && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />
            <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-80 overflow-y-auto">
              {charts.map(chart => {
                const selected = chart.id === activeChartId
                return (
                  <div
                    key={chart.id}
                    className={`flex items-center gap-1 px-2 py-1.5 ${
                      selected ? 'bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      className="flex-1 min-w-0 flex items-center gap-2 text-left px-1 py-1 rounded"
                      onClick={() => {
                        onSelect(chart.id)
                        setOpen(false)
                      }}
                    >
                      {chart.status === 'live' ? (
                        <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-green-100 text-green-800">
                          Live
                        </span>
                      ) : (
                        <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-amber-100 text-amber-800">
                          Draft
                        </span>
                      )}
                      <span className="truncate text-sm text-gray-800">{chart.name}</span>
                      {selected && <Check className="w-4 h-4 text-purple-600 flex-shrink-0 ml-auto" />}
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-gray-700 rounded"
                        title="Rename"
                        onClick={() => handleRename(chart)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isAdmin && chart.status === 'draft' && (
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-red-600 rounded"
                        title="Delete draft"
                        onClick={() => handleDelete(chart)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {isAdmin && (
        <>
          <button
            type="button"
            disabled={busy || !activeChartId}
            onClick={onCreateDraft}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            title="Create a draft copy of the current chart"
          >
            <Plus className="w-4 h-4" />
            New draft
          </button>
          {isViewingDraft && (
            <button
              type="button"
              disabled={busy}
              onClick={onPromoteToLive}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              title="Replace Live with this draft (current Live is saved as a draft)"
            >
              Make live
            </button>
          )}
        </>
      )}
    </div>
  )
}
